import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, TextField, CircularProgress, AutocompleteInputChangeReason, AutocompleteProps } from "@mui/material";

import LabelRequired from "./LabelRequired";
import { Field, Option } from "./types";
import { isString } from "../../utilities";

type LazyAutocompleteOption = Option;
const EMPTY_OPTIONS: LazyAutocompleteOption[] = [];

export interface LazyAutocompleteProps extends Pick<AutocompleteProps<LazyAutocompleteOption, boolean, boolean, boolean>, "disableClearable" | "disablePortal"> {
    // Properties
    field: Field;
    value: string;
    additional?: Record<string, unknown>;
    isRequired: boolean;
    formId?: string;
    language?: string;
    isLazySearch?: boolean;
    /** When true, the user can submit text that is not in the options list (MUI `freeSolo`). */
    freeSolo?: boolean;
    templateGroup?: string;

    // Actions
    onChange: (name: string, value: string, additional?: Record<string, unknown>) => void;
    onLoadedOptions?: (options: LazyAutocompleteOption[]) => void;
    fetchOptions: (keyword?: string) => Promise<LazyAutocompleteOption[]>;
}

function LazyAutocomplete(props: LazyAutocompleteProps) {
    const { field, value, additional: parentAdditional = {}, isRequired, onChange, formId, language, isLazySearch, freeSolo, templateGroup, onLoadedOptions, fetchOptions, ...autocompleteProps } = props;

    const [options, setOptions] = useState<LazyAutocompleteOption[]>(EMPTY_OPTIONS);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState(freeSolo && value ? value : "");
    const initialOptionsFetchedRef = useRef(false);

    const selectedOption = useMemo(
        () => options.find((o) => o.value === value || o.description === value
            || (
                templateGroup?.toLowerCase() === "general" &&
                o.additional?.username === value
            )
        ) ?? null,
        [options, value, templateGroup]
    );

    const autocompleteValue = useMemo((): LazyAutocompleteOption | string | null => {
        if (!value) return null;
        if (freeSolo && !selectedOption) {
            return value;
        }
        return selectedOption;
    }, [freeSolo, value, selectedOption]);

    const loadOptions = useCallback(async (keyword?: string) => {
        setLoading(true);
        try {
            const list = await fetchOptions(keyword);
            setOptions(list ?? EMPTY_OPTIONS);
            onLoadedOptions?.(list ?? EMPTY_OPTIONS);
        } catch {
            setOptions(EMPTY_OPTIONS);
        } finally {
            setLoading(false);
        }
    }, [formId, language, fetchOptions, onLoadedOptions]);

    const handleChange = useCallback(
        (_: unknown, newValue: LazyAutocompleteOption | string | null) => {
            console.log("[LazyAutocomplete] Selected option:", newValue);
            let value, description;
            let additional = parentAdditional;
            if (isString(newValue)) {
                value = newValue;
                description = newValue;
            } else {
                value = newValue?.value ?? "";
                description = newValue?.description ?? "";
                additional = value ? { ...parentAdditional, ...(newValue?.additional ?? {}) } : {};
            }

            console.log("[LazyAutocomplete] Parsed selection:", {
                fieldName: field.name,
                value,
                description,
                additional,
            });

            setInputValue(description);
            onChange(field.name, value, additional);
        },
        [field.name, onChange, parentAdditional]
    );

    const handleInputChange = useCallback(
        (_: unknown, v: string, reason: AutocompleteInputChangeReason) => {
            if (freeSolo && (reason === "input" || reason === "clear")) {
                setInputValue(v);
                onChange(field.name, v, v ? { ...parentAdditional, description: v } : undefined);
                return;
            }
            if (reason !== "input") return;
            setInputValue(v);
            if (!v) onChange(field.name, "", undefined);
        },
        [field.name, onChange, freeSolo, parentAdditional]
    );

    useEffect(() => {
        if (
            !value ||
            value === inputValue ||
            value === selectedOption?.value ||
            value === selectedOption?.description
        ) return;

        loadOptions(value);
    }, [value, formId, language]);

    useEffect(() => {
        console.log("[LazyAutocomplete] Auto-select effect:", {
            fieldName: field.name,
            value,
            inputValue,
            selectedOption,
            optionsCount: options.length,
        });
        if (
            freeSolo ||
            !value ||
            inputValue ||
            options.length === 0 ||
            selectedOption?.description === inputValue
        ) return;

        setInputValue(selectedOption?.description ?? "");

        if (
            selectedOption &&
            selectedOption.value !== value
        ) {
            onChange(
                field.name,
                selectedOption.value,
                {
                    ...parentAdditional,
                    ...(selectedOption.additional ?? {}),
                }
            );
        }
    }, [value, inputValue, options, freeSolo, selectedOption, onChange]);

    useEffect(() => {
        if (!freeSolo) return;
        if (!selectedOption) return;

        setInputValue(selectedOption.description);
    }, [freeSolo, selectedOption]);

    useEffect(() => {
        initialOptionsFetchedRef.current = false;
    }, [formId, language, isLazySearch, fetchOptions]);

    // Load static option lists once (including when the API returns an empty array).
    useEffect(() => {
        if (isLazySearch || initialOptionsFetchedRef.current) return;
        initialOptionsFetchedRef.current = true;
        void loadOptions();
    }, [isLazySearch, loadOptions]);

    // Load options for Doctor/Witness/Interpreter/Procedures when user types (debounced)
    const lastKeywordRef = useRef("");
    useEffect(() => {
        if (!isLazySearch) return;

        if (inputValue.length < 2) {
            if (!freeSolo) {
                setOptions(EMPTY_OPTIONS);
            }
            return;
        }

        if (lastKeywordRef.current === inputValue) {
            return;
        }

        lastKeywordRef.current = inputValue;

        const t = setTimeout(() => {
            loadOptions(inputValue);
        }, 300);

        return () => clearTimeout(t);
    }, [inputValue, isLazySearch, freeSolo, loadOptions]);

    useEffect(() => {
        if (value) return;

        setInputValue("");
    }, [value]);

    return (
        <Autocomplete<LazyAutocompleteOption, false, boolean, boolean>
            disablePortal
            freeSolo={!!freeSolo}
            value={autocompleteValue as LazyAutocompleteOption}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            isOptionEqualToValue={(option, val) => {
                if (isString(val)) {
                    return option.value === val || option.description === val ||
                            option.additional?.username === val;
                }
                return option.value === val?.value || option.additional?.username === val?.value;
            }}
            options={options}
            getOptionLabel={(option) =>
                isString(option) ? option : option.description
            }
            loading={loading}
            fullWidth
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={<LabelRequired label={field.label} isRequired={isRequired} />}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? (
                                    <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            onChange={handleChange}
            {...autocompleteProps}
        />
    );
}

export default LazyAutocomplete;
